import { useAuth } from "@/context/AuthContext";
import { Menu, Search, Loader2, Wrench, FileText, QrCode, Bell, X, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/credits";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/search", { params: { q: q.trim() } });
        setResults(data);
        setOpen(true);
      } catch (e) { setResults(null); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };

  const empty = results && !results.installers.length && !results.certificates.length && !results.bottle_codes.length;

  return (
    <div ref={boxRef} className="relative hidden md:block" data-testid="global-search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Search installers, certificates, VIN, codes…"
        className="h-9 w-72 rounded-lg border border-white/[0.08] bg-[#141414] pl-9 pr-8 text-sm text-white placeholder:text-neutral-600 focus:border-[#E30613] focus:outline-none focus:ring-1 focus:ring-[#E30613]"
        data-testid="global-search-input"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-500" />}
      {open && results && (
        <div className="absolute right-0 top-11 z-50 max-h-[70vh] w-96 overflow-y-auto rounded-xl border border-white/[0.08] bg-[#141414] p-2 shadow-2xl" data-testid="global-search-results">
          {empty && <p className="px-3 py-6 text-center text-sm text-neutral-500">No matches for “{q}”.</p>}
          {results.installers.length > 0 && (
            <Group title="Installers">
              {results.installers.map((i) => (
                <button key={i.id} onClick={() => go(`/installers/${i.id}`)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/[0.05]" data-testid="search-result-installer">
                  <Wrench className="h-4 w-4 text-[#FF1C2A]" />
                  <div><p className="text-sm text-white">{i.business_name}</p><p className="text-xs text-neutral-500">{i.email} · {i.status}</p></div>
                </button>
              ))}
            </Group>
          )}
          {results.certificates.length > 0 && (
            <Group title="Certificates">
              {results.certificates.map((c) => (
                <button key={c.id} onClick={() => go(`/certificates/${c.id}`)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/[0.05]" data-testid="search-result-certificate">
                  <FileText className="h-4 w-4 text-neutral-400" />
                  <div><p className="font-mono text-sm text-white">{c.certificate_number}</p><p className="text-xs text-neutral-500">{c.customer_name} · {c.vin}</p></div>
                </button>
              ))}
            </Group>
          )}
          {results.bottle_codes.length > 0 && (
            <Group title="Bottle Codes">
              {results.bottle_codes.map((b) => (
                <button key={b.id} onClick={() => go(`/bottle-batches`)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/[0.05]" data-testid="search-result-code">
                  <QrCode className="h-4 w-4 text-neutral-400" />
                  <div><p className="font-mono text-sm text-white">{b.code}</p><p className="text-xs text-neutral-500 capitalize">{b.status}</p></div>
                </button>
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="mb-1">
      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">{title}</p>
      {children}
    </div>
  );
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then(({ data }) => {
    setItems(data.items || []);
    setUnread(data.unread_count || 0);
  }).catch(() => {});

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const dismiss = async (id) => {
    await api.post(`/notifications/${id}/dismiss`).catch(() => {});
    setItems((prev) => prev.filter((n) => n.id !== id));
    setUnread((u) => Math.max(0, u - 1));
  };

  const toggle = () => { const next = !open; setOpen(next); if (next) load(); };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      <button onClick={toggle} className="relative rounded-lg p-2 text-neutral-400 transition-colors hover:bg-white/[0.05] hover:text-white" data-testid="notification-trigger" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}>
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E30613] px-1 text-[9px] font-bold text-white" data-testid="notification-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 max-h-[70vh] w-80 overflow-y-auto rounded-xl border border-white/[0.08] bg-[#141414] shadow-2xl" data-testid="notification-panel">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Notifications</p>
            {items.length > 0 && <button onClick={markAllRead} className="text-xs text-[#FF6069] hover:text-[#FF1C2A]" data-testid="mark-all-read">Mark all read</button>}
          </div>
          <div className="p-2">
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-neutral-500">You're all caught up.</p>
            ) : items.map((n) => (
              <div key={n.id} className={`group flex items-start justify-between gap-2 rounded-lg px-3 py-2.5 hover:bg-white/[0.04] ${n.read ? "" : "bg-white/[0.02]"}`} data-testid="notification-item">
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E30613]" />}
                  <div>
                    <p className="text-sm text-white">{n.description}</p>
                    {n.cta_label && n.cta_url && (
                      <a href={n.cta_url} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-[#E30613] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#FF1C2A]" data-testid="notification-cta">
                        <ExternalLink className="h-3 w-3" /> {n.cta_label}
                      </a>
                    )}
                    <p className="mt-0.5 text-xs text-neutral-600">{fmtDate(n.created_at, true)}</p>
                  </div>
                </div>
                <button onClick={() => dismiss(n.id)} className="shrink-0 text-neutral-600 opacity-0 transition-opacity hover:text-white group-hover:opacity-100" data-testid="dismiss-notification" aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Topbar({ title, onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.contact_name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-white/[0.06] bg-[#0A0A0A]/80 px-5 backdrop-blur-xl md:px-8"
      data-testid="topbar"
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="rounded-lg p-2 text-neutral-400 hover:bg-white/[0.05] hover:text-white md:hidden"
          data-testid="menu-toggle"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight text-white" data-testid="page-title">
            {title}
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {user?.role === "super_admin" && <GlobalSearch />}
        <NotificationBell />
        <span className="hidden rounded-full border border-white/[0.08] px-3 py-1 text-xs font-medium text-neutral-400 sm:inline">
          {user?.role === "super_admin" ? "Super Admin" : "Installer"}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-3 rounded-full pl-1 pr-3 py-1 transition-colors duration-200 hover:bg-white/[0.05]"
              data-testid="user-menu-trigger"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E30613]/15 text-xs font-semibold text-[#FF1C2A]">
                {initials}
              </span>
              <span className="hidden text-sm font-medium text-neutral-200 sm:inline">
                {user?.contact_name || user?.business_name}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 border-white/[0.08] bg-[#141414] text-neutral-200"
          >
            <DropdownMenuLabel className="text-neutral-400">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-white">{user?.business_name}</span>
                <span className="text-xs font-normal text-neutral-500">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/[0.06]" />
            <DropdownMenuItem
              onClick={() => navigate(user?.role === "super_admin" ? "/settings" : "/profile")}
              className="cursor-pointer focus:bg-white/[0.06]"
              data-testid="menu-profile"
            >
              {user?.role === "super_admin" ? "Settings" : "Profile"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-[#FF1C2A] focus:bg-[#E30613]/10 focus:text-[#FF1C2A]"
              data-testid="menu-logout"
            >
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
